<script setup lang="ts">
import { useData } from 'vike-vue/useData'
import { useI18n } from 'vue-i18n'
import type { ArticlePageData } from './+data'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted } from '@/composables/useGtmTag'

const { t } = useI18n()

const data = useData<ArticlePageData>()
useFormatSeoContent(data.article.seoTitle, data.article.description)
useGtmOnPageMounted('article')

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    { text: t('page.conseils'), to: '/conseils' },
    {
      text:
        data.article?.breadcrumbTitle && data.article.breadcrumbTitle.length > 40
          ? `${data.article.breadcrumbTitle.slice(0, 40)}...`
          : data.article?.breadcrumbTitle,
    },
  ],
}
</script>

<template>
  <div
    v-if="data.article"
    class="Article"
  >
    <molecules-ui-breadcrumb v-bind="breadcrumb" />
    <div class="Article__container container">
      <section class="Article__grid grid">
        <p class="Article__readingTime mb-5 color-red-01">
          <span class="heading-3 color-blue-01">
            {{ t('article.readingTime') }}
          </span>
          &nbsp;
          <span class="paragraph-3 color-red-01">
            {{ data.article.readingTime }}
          </span>
        </p>
        <aside class="Article__aside gy-5">
          <molecules-ui-profile
            v-if="data.article.contributor"
            v-bind="data.article.contributor"
          />
          <div
            v-if="data.article.relatedArticles"
            class="Article__related px-4"
          >
            <template
              v-for="(relatedArticle, index) in data.article.relatedArticles"
              :key="relatedArticle.title"
            >
              <molecules-ui-preview
                :title="relatedArticle.title"
                :date="relatedArticle.date"
                :description="relatedArticle.description"
                :url="relatedArticle.url"
                :tag="relatedArticle.tag"
              />
              <hr
                v-if="index < data.article.relatedArticles.length - 1"
                class="Article__related__separator my-4"
              />
            </template>
          </div>
        </aside>
        <div class="Article__content gy-5 color-blue-01">
          <div class="Article__head">
            <p class="mb-2 color-red-01">{{ data.article.date }}</p>
            <h1 class="mb-2 heading-1">{{ data.article.title }}</h1>
            <p class="paragraph-1">{{ data.article.description }}</p>
          </div>
          <atoms-ui-image
            v-if="data.article.image"
            class="Article__image"
            v-bind="data.article.image"
          />
          <molecules-ui-rich-text
            v-if="data.article.content"
            :content="data.article.content"
          />
          <molecules-ui-highlight
            v-if="data.article.highlight"
            v-bind="data.article.highlight"
            class="Article__highlight"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/colors';
@use 'config/spaces';
@use 'utilities/mq';

@forward 'utilities/text';

.Article__container {
  margin-block-start: map.get(spaces.$sizes, '7');
  margin-block-end: map.get(spaces.$sizes, '9');

  @include mq.media('>=small') {
    margin-block: map.get(spaces.$sizes, '10');
  }
}

.Article__grid {
  grid-template-rows: auto 1fr;
}

.Article__aside {
  grid-column: span 4;
  grid-row: 3;
  display: flex;
  flex-direction: column;

  @include mq.media('>=medium') {
    grid-column: 1 / span 4;
    grid-row: 2;
  }
}

.Article__readingTime {
  grid-row: 1;
  grid-column: 1 / span 4;
  height: fit-content;

  @include mq.media('>=medium') {
    padding: 0 map.get(spaces.$sizes, '5');
  }
}

.Article__related__separator {
  width: 100%;
  border: 1px solid map.get(colors.$blue, '03');
}

.Article__content {
  grid-column: span 4;
  grid-row: 2;
  display: flex;
  flex-direction: column;

  @include mq.media('<medium') {
    margin-bottom: map.get(spaces.$sizes, '8');
  }

  @include mq.media('>=medium') {
    grid-column: span 8;
    grid-row: span 2;
  }
}

.Article__image {
  @include mq.media('<small') {
    height: 300px;
    object-fit: cover;
  }
}

.Article__highlight {
  :deep(.Highlight__title) {
    @extend .heading-3;
  }

  :deep(.Highlight__description) {
    @extend .paragraph-3;
  }

  @include mq.media('>=medium') {
    flex-direction: row;

    :deep(.Button) {
      align-self: center;
      flex-shrink: 0;
      margin-top: 0;
    }
  }
}
</style>
